import { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  VStack,
  Heading,
  Box,
  HStack,
  Divider,
} from '@chakra-ui/react';
import Select from 'react-select';

import { useTypedSelector } from '../../../hooks';
import {
  useGetTeamMembersQuery,
  useAssignTeamMemberToPhaseMutation,
  useGetTeamMembersByPhaseIdQuery,
} from '../../../services';
import { CenteredSpinner, UserData } from '../../../components';
import { useParams } from 'react-router-dom';
import { TeamMember } from '../../../models';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

export const AssignTeamMemberModal = ({
  isOpen,
  onClose,
  phaseId,
}: {
  isOpen: boolean;
  onClose: () => void;
  phaseId: string;
}) => {
  const [filterUsers, setFilterUsers] = useState<TeamMember[]>([]);
  const [teamMemberId, setTeamMemberId] = useState<number | undefined>(
    undefined
  );
  const { userDetails } = useTypedSelector(state => state.auth);
  const { phaseId: phaseIdForMultiPhases, projectId } = useParams();

  const { data: allUsers, isLoading: isAllUsersLoading } =
    useGetTeamMembersQuery(
      {
        page: 0,
        size: 10000,
        companyId: Number(userDetails?.companyBasicInfo.id),
      },
      {
        skip: !userDetails,
      }
    );

  const { data: projectTeamMembers, isLoading: isProjectTeamMembersLoading } =
    useGetTeamMembersByPhaseIdQuery({
      projectId: String(projectId),
      phaseId,
      page: 0,
      size: 1000,
      name: '',
    });

  const [assignTeamMemberToPhase, { isLoading: isReassignLoading }] =
    useAssignTeamMemberToPhaseMutation();

  const handleAssignTeamMember = async () => {
    const response = await assignTeamMemberToPhase({
      teamMemberId: String(teamMemberId),
      phaseId: phaseIdForMultiPhases
        ? String(phaseIdForMultiPhases)
        : undefined,
      projectId: String(projectId),
    });

    // A rejected assignment used to close the modal as if the member had been
    // assigned. Keep it open with the selection intact instead.
    if (!isMutationSuccess(response)) {
      notifyMutationFailure(response, {
        title: 'Could not assign this member',
        description:
          'The member was not assigned. Please try again in a moment.',
      });
      return;
    }

    onClose();
  };

  useEffect(() => {
    if (projectTeamMembers?.data && allUsers?.data) {
      setFilterUsers(() => {
        if (!projectTeamMembers.data.content.length) {
          return allUsers?.data.content;
        } else {
          return (
            allUsers.data.content.filter(
              user =>
                !projectTeamMembers.data.content
                  .map(({ id }) => id)
                  .includes(user.id)
            ) || []
          );
        }
      });
    }
  }, [allUsers?.data, projectTeamMembers?.data]);

  return (
    <>
      <Modal blockScrollOnMount={false} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />

        <ModalContent>
          <Box mt="6">
            <ModalCloseButton />
            {isAllUsersLoading || isProjectTeamMembersLoading ? (
              <CenteredSpinner />
            ) : (
              <ModalBody>
                <VStack align="start" color="custom_blue.1">
                  <Heading fontSize="md" fontWeight="semibold">
                    Assign This {phaseIdForMultiPhases ? 'Phase' : 'Project'} to
                  </Heading>

                  <Box w="full">
                    <Select
                      onChange={e => {
                        setTeamMemberId(e?.value);
                      }}
                      options={filterUsers.map(
                        ({ id, firstName, lastName }) => ({
                          value: id,
                          label: `${firstName} ${lastName}`,
                        })
                      )}
                    />
                  </Box>

                  <Heading fontSize="md" fontWeight="semibold" mt="6">
                    People / Roles with Access
                  </Heading>
                  {filterUsers.map(
                    (
                      {
                        id,
                        firstName,
                        lastName,
                        profilePictureThumbnailURL,
                        role,
                      },
                      index
                    ) => (
                      <Box key={id} w="full">
                        <HStack
                          px="3"
                          py="2"
                          cursor="pointer"
                          borderRadius="md"
                          onClick={() => setTeamMemberId(id)}
                          bg={id === teamMemberId ? 'warning' : 'transparent'}
                        >
                          <UserData
                            roleName={role.name}
                            firstName={firstName}
                            lastName={lastName}
                            pictureUrl={profilePictureThumbnailURL}
                          />
                        </HStack>
                        {index !== filterUsers.length - 1 && <Divider />}
                      </Box>
                    )
                  )}
                </VStack>
              </ModalBody>
            )}
          </Box>
          <ModalFooter display="flex" flexDir="column" gap="2">
            <Button
              bg="error.600"
              _hover={{ bg: 'error.600' }}
              color="white"
              w="full"
              onClick={handleAssignTeamMember}
              isDisabled={!teamMemberId}
              isLoading={isReassignLoading}
            >
              Assign
            </Button>
            <Button
              onClick={onClose}
              w="full"
              color="custom_blue.1"
              fontWeight="semibold"
              fontSize="sm"
              boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
              border="1px"
              borderColor="custom_blue.3"
              bg="none"
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
